import {
  apiUrl,
  TENANT_ID,
  BUSINESS_UNIT_ID,
} from "@/config/api";

export type WorkstationOperatingDay = {
  day?: string;
  hours?: string[];
};

export type Workstation = {
  name?: string;
  description?: string | null;
  latitude?: string;
  longitude?: string;
  city?: string;
  address?: string;
  logoImage?: string;
  operatingHours?: WorkstationOperatingDay[];
  distance?: string;
  phoneNumber?: string | null;
  rating?: string;
  reviewCount?: number;
};

export type WorkstationsApiResponse = {
  success?: boolean;
  message?: string;
  result?: Workstation[];
  total?: number;
  page?: number;
  limit?: number;
};

export type GetWorkstationsBody = {
  latitude: string;
  longitude: string;
  page: number;
  limit: number;
  langCode: "en" | "ar";
  /** When set, upstream filters workshops that offer these services (e.g. from service detail). */
  serviceIds?: string[];
  /** When both are set, upstream `get-all` filters by service category / subcategory (nearest centers). */
  categoryId?: string;
  subCategoryId?: string;
};

/** Server-passed first page for instant paint on /find-workstation */
export type WorkstationSeed = {
  items: Workstation[];
  total: number;
  page: number;
};

/** Default map center (Riyadh) — matches CMS workstation search. */
export const DEFAULT_WORKSTATION_COORDS = {
  latitude: "24.71570516",
  longitude: "46.67407691",
} as const;

export async function fetchWorkstations(
  body: GetWorkstationsBody
): Promise<WorkstationsApiResponse | null> {
  const payload: Record<string, unknown> = {
    latitude: body.latitude,
    longitude: body.longitude,
    page: body.page,
    limit: body.limit,
    langCode: body.langCode,
  };
  const ids = body.serviceIds?.map((id) => id.trim()).filter(Boolean);
  if (ids && ids.length > 0) {
    payload.serviceIds = ids;
  }

  const categoryId = body.categoryId?.trim();
  const subCategoryId = body.subCategoryId?.trim();
  if (categoryId && subCategoryId) {
    payload.categoryId = categoryId;
    payload.subCategoryId = subCategoryId;
  }

  const res = await fetch(apiUrl("/v1/workstations/get-all"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-tenant-id": TENANT_ID,
      "x-business-unit-id": BUSINESS_UNIT_ID,
    },
    body: JSON.stringify(payload),
    cache: "no-store",
  });

  if (!res.ok) return null;

  const data = (await res.json()) as WorkstationsApiResponse;
  if (!Array.isArray(data.result)) return null;

  return {
    success: data.success !== false,
    message: data.message,
    result: data.result,
    total: data.total ?? 0,
    page: data.page ?? body.page,
    limit: data.limit ?? body.limit,
  };
}
